/**
 * Authentication Initialization - handles Auth0 flow initialization
 */

import type { Config } from '../modules/config';
import type { PortalUser } from '../types';
import { applyUser, parseFragmentTokens, exchangePkceAuth, getUserProfile } from '../modules/auth';
import { deriveSuperadmin, resolveRoleEnv } from '../modules/roles';
import { loadStoredAuth, saveAuth, clearAuth } from '../modules/state';
import { storeAuthInSessionStorage } from '../modules/secureAuthStorage';
import { showAuth, showApp } from '../modules/ui';
import { resetRemoteSnapshot } from '../modules/lotOperations';
import { fetchAndPopulateUsers } from '../modules/superadmin';
import { applyProfileToLocalSettings } from '../coordination/stateSync';
import { createLogger } from '../../shared/logger';

const log = createLogger('portal:auth-init');

interface AuthInitDependencies {
  state: any; // Using 'any' to avoid importing full State type
  cfg: Config | null;
  applyModeBanner: () => void;
  startRemoteLotsFetch: () => void;
  scheduleCreditPolling: () => void;
  applyPendingProfile: () => Promise<void>;
  cancelPolling: () => void;
  cancelCreditPolling: () => void;
  renderUserIdentity: () => void;
  hideAuthError: () => void;
  wasLoggedOut: boolean;
}

function scheduleLoginPendingUi(label: string): () => void {
  const loginButton = document.getElementById('connect');
  if (!(loginButton instanceof HTMLButtonElement)) return () => {};

  const originalText = loginButton.textContent;
  const originalDisabled = loginButton.disabled;
  const originalPending = loginButton.dataset.pending;
  const originalAriaBusy = loginButton.getAttribute('aria-busy');

  const timer = window.setTimeout(() => {
    if (!loginButton.isConnected) return;
    loginButton.dataset.pending = 'true';
    loginButton.disabled = true;
    loginButton.setAttribute('aria-busy', 'true');
    loginButton.textContent = label;
  }, 250);

  return () => {
    clearTimeout(timer);
    if (!loginButton.isConnected) return;
    // Only restore if we're still on the auth screen.
    const authCard = document.getElementById('auth-card');
    if (!authCard || authCard.hidden) return;
    loginButton.disabled = originalDisabled;
    if (typeof originalPending === 'string') loginButton.dataset.pending = originalPending;
    else delete loginButton.dataset.pending;
    if (originalAriaBusy === null) loginButton.removeAttribute('aria-busy');
    else loginButton.setAttribute('aria-busy', originalAriaBusy);
    if (originalText !== null) loginButton.textContent = originalText;
  };
}

/**
 * Complete Auth0 SPA-SDK (PKCE) authentication
 */
async function checkAzureSWAAuth(cfg: Config | null) {
  if (!cfg) return null;
  return await exchangePkceAuth(cfg);
}

/**
 * Initialize Auth0 authentication flow
 * Handles stored tokens, SWA auth, fragment tokens, and fallback to login
 */
export async function initAuth0(deps: AuthInitDependencies): Promise<void> {
  const authInitStart = Date.now();
  log.info('[auth-init] Starting authentication initialization');

  const {
    state,
    cfg,
    applyModeBanner,
    startRemoteLotsFetch,
    scheduleCreditPolling,
    applyPendingProfile,
    cancelPolling,
    cancelCreditPolling,
    renderUserIdentity,
    hideAuthError,
    wasLoggedOut,
  } = deps;

  log.info(`[auth-init] wasLoggedOut flag: ${wasLoggedOut}`);

  // Check if Auth0 is properly configured (not just present but valid)
  const hasAuth0 = !!(
    cfg?.auth0Domain &&
    cfg?.auth0ClientId &&
    cfg.auth0ClientId !== 'PLACEHOLDER_CLIENT_ID' &&
    !cfg.auth0ClientId.includes('PLACEHOLDER')
  );

  if (!hasAuth0) {
    log.info('[auth-init] No valid Auth0 config, showing landing page in local mode');
    state.mode = 'local';
    resetRemoteSnapshot();
    // Show landing page so new visitors can see info about the extension
    // Hide sign-in button since Auth0 is not configured
    const signInBtn = document.getElementById('connect');
    if (signInBtn) {
      (signInBtn as HTMLElement).hidden = true;
    }
    const heroSignInBtn = document.getElementById('hero-signin');
    if (heroSignInBtn) {
      (heroSignInBtn as HTMLElement).hidden = true;
    }
    showAuth(); // Show landing page instead of jumping to app
    applyModeBanner();
    cancelPolling();
    applyUser(null);
    renderUserIdentity();
    return;
  }

  hideAuthError();

  // Check if we have valid existing token (skip if user explicitly logged out)
  if (
    !wasLoggedOut &&
    state.mode === 'auth0' &&
    state.extAccessToken &&
    Date.now() < state.extExpiresAt
  ) {
    log.info(
      `[auth-init] Valid stored token found, fetching fresh profile (${Date.now() - authInitStart}ms)`
    );

    // CRITICAL: Always fetch fresh user profile to ensure superadmin status is current
    try {
      const freshProfile = await getUserProfile(cfg);
      if (freshProfile) {
        log.info('[auth-init] Fresh profile fetched successfully');
        applyUser(freshProfile);
        renderUserIdentity();

        // Apply profile settings (timeZone, homePostalCode) to local state
        // This ensures settings persist across devices and sessions
        applyProfileToLocalSettings(freshProfile);

        // Update stored auth with fresh profile
        const authPayload = {
          provider: 'auth0',
          tokenType: 'Bearer',
          accessToken: state.extAccessToken || '',
          refreshToken: state.extRefreshToken || undefined,
          expiresAt: state.extExpiresAt,
          user: state.user ?? freshProfile,
        };
        saveAuth(authPayload);
        // Store in sessionStorage as well for cross-tab recovery (if needed)
        storeAuthInSessionStorage(authPayload);
      } else {
        log.warn('[auth-init] Failed to fetch fresh profile, using stored user data');
      }
    } catch (err) {
      log.warn('[auth-init] Error fetching fresh profile:', err);
    }

    resetRemoteSnapshot();
    showApp();
    applyModeBanner();
    await applyPendingProfile();
    startRemoteLotsFetch();
    scheduleCreditPolling();
    // If starting in SUPERADMIN mode (e.g., persisted preference) fetch users immediately
    if (state.user?.isSuperUser && state.viewMode === 'superadmin') {
      void fetchAndPopulateUsers(cfg);
    }
    log.info(`[auth-init] Completed with stored token in ${Date.now() - authInitStart}ms`);
    return;
  }

  applyUser(null);
  renderUserIdentity();

  // Complete Auth0 PKCE authentication (skip if user explicitly logged out)
  if (!wasLoggedOut) {
    log.info('[auth-init] Attempting Auth0 PKCE auth');
    try {
      const cleanupPendingUi = scheduleLoginPendingUi('Signing you in…');
      const swaAuthResult = await checkAzureSWAAuth(cfg);
      cleanupPendingUi();
      if (swaAuthResult && swaAuthResult.accessToken) {
        log.info(
          `[auth-init] SWA auth successful, setting up session (${Date.now() - authInitStart}ms)`
        );
        state.extAccessToken = swaAuthResult.accessToken;
        state.extRefreshToken = swaAuthResult.refreshToken || null;
        const expiresIn = Number(swaAuthResult.expiresIn);
        state.extExpiresAt =
          Number.isFinite(expiresIn) && expiresIn > 0 ? Date.now() + expiresIn * 1000 - 30000 : 0;

        const candidateUser = swaAuthResult.user || null;
        if (candidateUser && swaAuthResult.idTokenPayload) {
          // The backend (token-exchange) is the source of truth for superadmin
          // status; it may grant it via the verified email-domain allowlist even
          // when the Auth0 ID token carries no isSuperUser claim. Only elevate
          // here via client-side derivation — never downgrade the backend value.
          candidateUser.isSuperUser =
            Boolean(candidateUser.isSuperUser) ||
            deriveSuperadmin(candidateUser, swaAuthResult.idTokenPayload, resolveRoleEnv());
        }
        applyUser(candidateUser);
        renderUserIdentity();
        const authPayload = {
          provider: 'auth0',
          tokenType: 'Bearer',
          accessToken: state.extAccessToken || '',
          refreshToken: state.extRefreshToken || undefined,
          scopes: [],
          expiresAt: state.extExpiresAt,
          user: state.user ?? candidateUser ?? undefined,
          account: candidateUser
            ? {
                userId: candidateUser.sub,
                email: candidateUser.email,
                name: candidateUser.name,
                subject: candidateUser.sub,
                isSuperUser: candidateUser.isSuperUser,
              }
            : undefined,
        };
        saveAuth(authPayload);
        // Store in sessionStorage as bridge for recovery during auth flow redirects
        storeAuthInSessionStorage(authPayload);
        state.mode = 'auth0';
        resetRemoteSnapshot();
        showApp();
        applyModeBanner();
        await applyPendingProfile();

        // Fetch full profile to apply server-side settings (timeZone, homePostalCode)
        try {
          const fullProfile = await getUserProfile(cfg);
          if (fullProfile) {
            applyProfileToLocalSettings(fullProfile);
          }
        } catch {
          // Silent fail - settings sync not critical for initial login
        }

        startRemoteLotsFetch();
        scheduleCreditPolling();
        log.info(`[auth-init] Completed with SWA auth in ${Date.now() - authInitStart}ms`);
        return;
      }
    } catch (err) {
      log.warn('[auth-init] SWA auth attempt failed:', err);
      // SWA auth failed, continue to next method
    }
  } else {
    log.info('[auth-init] Skipping Auth0 PKCE auth due to explicit logout');
  }

  // Try stored authentication
  log.info('[auth-init] Checking stored authentication');
  const storedAuth = loadStoredAuth();
  if (storedAuth && storedAuth.accessToken && !wasLoggedOut) {
    state.extAccessToken = storedAuth.accessToken;
    state.extRefreshToken = storedAuth.refreshToken || null;
    state.extExpiresAt = storedAuth.expiresAt || 0;
    const storedUser: PortalUser | null = storedAuth.account
      ? {
          sub:
            typeof storedAuth.account.userId === 'string'
              ? storedAuth.account.userId
              : typeof storedAuth.account.subject === 'string'
                ? storedAuth.account.subject
                : undefined,
          email: storedAuth.account.email,
          name: storedAuth.account.name,
          isSuperUser: Boolean(storedAuth.account.isSuperUser),
        }
      : null;
    if (Date.now() < state.extExpiresAt) {
      log.info(`[auth-init] Stored auth is valid, using it (${Date.now() - authInitStart}ms)`);
      applyUser(storedUser);
      renderUserIdentity();
      state.mode = 'auth0';
      resetRemoteSnapshot();
      showApp();
      applyModeBanner();
      await applyPendingProfile();

      // Fetch full profile to apply server-side settings (timeZone, homePostalCode)
      try {
        const fullProfile = await getUserProfile(cfg);
        if (fullProfile) {
          applyProfileToLocalSettings(fullProfile);
        }
      } catch {
        // Silent fail - settings sync not critical
      }

      startRemoteLotsFetch();
      scheduleCreditPolling();
      log.info(`[auth-init] Completed with stored auth in ${Date.now() - authInitStart}ms`);
      return;
    } else {
      log.info('[auth-init] Stored auth expired');
    }
  }

  // Try capturing tokens from URL fragment
  log.info('[auth-init] Checking for URL fragment tokens');
  const captured = !wasLoggedOut && parseFragmentTokens();
  if (captured) {
    log.info(`[auth-init] URL fragment tokens captured (${Date.now() - authInitStart}ms)`);
    renderUserIdentity();
    state.mode = 'auth0';
    resetRemoteSnapshot();
    showApp();
    applyModeBanner();
    await applyPendingProfile();

    // Fetch full profile to apply server-side settings (timeZone, homePostalCode)
    try {
      const fullProfile = await getUserProfile(cfg);
      if (fullProfile) {
        applyProfileToLocalSettings(fullProfile);
      }
    } catch {
      // Silent fail - settings sync not critical
    }

    startRemoteLotsFetch();
    scheduleCreditPolling();
    log.info(`[auth-init] Completed with fragment tokens in ${Date.now() - authInitStart}ms`);
    return;
  }

  // Fall back to local mode but keep auth screen visible so the user can choose sign-in or local
  log.info(
    `[auth-init] No auth available, showing auth card in local mode (${Date.now() - authInitStart}ms)`
  );
  state.mode = 'local';
  resetRemoteSnapshot();
  showAuth();
  applyModeBanner();
  cancelPolling();
  cancelCreditPolling();
  applyUser(null);
  renderUserIdentity();
}

/**
 * Check if user was logged out (logout flag in localStorage or logged_out query param)
 * Returns true if logout flag was present and cleans up
 */
export function checkLogoutFlag(): boolean {
  try {
    // Check for logged_out query parameter (from Auth0 redirect after logout)
    const urlParams = new URLSearchParams(window.location.search);
    const hasLoggedOutParam = urlParams.has('logged_out');

    if (hasLoggedOutParam) {
      // Clean up the URL by removing the query parameter
      urlParams.delete('logged_out');
      const newSearch = urlParams.toString();
      const newUrl = window.location.pathname + (newSearch ? '?' + newSearch : '');
      window.history.replaceState({}, document.title, newUrl);

      try {
        localStorage.removeItem('lotlingo.portal.logoutFlag.v1');
      } catch {
        // ignore storage errors
      }

      clearAuth();
      log.info('[auth-init] logged_out query param detected, cleared auth and returning true');
      return true;
    }

    // Check for logout flag in localStorage (fallback for immediate redirect handling)
    const logoutFlag = localStorage.getItem('lotlingo.portal.logoutFlag.v1');
    log.info(`[auth-init] Logout flag check: ${logoutFlag ? 'PRESENT' : 'NOT PRESENT'}`);
    if (logoutFlag) {
      localStorage.removeItem('lotlingo.portal.logoutFlag.v1');
      clearAuth();
      log.info('[auth-init] Logout flag was set, cleared auth and returning true');
      return true;
    }
  } catch {
    // localStorage or URL access failed
  }
  return false;
}
